// Landing an email's attachments — the ONE path, shared by a dropped file and a paste.
//
// ⛔ IT FOLLOWS uploadNewAttachment (TaskEngine.js), IT DOES NOT INVENT A SECOND HOME. Same R2
// upload via presigned PUT, same fully UNSCOPED file_exchange row (direction 'ntrg_internal',
// folder 'Task Attachments', no client/property/parcel), same task_attachments join. A file landed
// here surfaces only on its task — never in a client's Filed Documents, Intake, or a portal.
//
// ⛔ UPLOADS HAPPEN BEFORE ANY TASK EXISTS, AND THAT IS THE WHOLE POINT. A task created from an
// email whose attachments then failed to upload is a task that LOOKS complete and is not — the
// exact failure this codebase has spent weeks removing. So: stage every file first, and only if
// EVERY ONE lands do the tasks get created. If any upload fails, the staged objects are deleted and
// nothing is created at all.
//
// The cost of that ordering, stated: the storage key cannot contain the task id, because there is
// no task yet. Files live under tasks/_inbox/<batch>/ and the task_attachments rows are written
// afterwards. The key is provenance, not the link — the link is the join table, and that is already
// how attachExisting() works for a file that lives somewhere else entirely.

import { supabase } from '../supabase';
import { uploadFile } from './uploadFile';
import { fileDelete } from './fileDelete';
import { dedupeAttachments, looksDecorative, MAX_ATTACHMENTS } from './emailParse';
import { DOC_KEYS } from './docVocab';

const safeName = (n) => String(n || 'attachment').replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 120);

// ⛔ OUTLOOK NAMES EVERY PASTED SCREENSHOT image001.png. Three emails each contributing one produce
// three files called image001.png on the same task, and nobody can tell which is which — the
// attachment list becomes useless at exactly the moment it matters. Generic names are therefore
// REPLACED with something that says where the image came from; real filenames are left alone,
// because "notice.pdf" is what the sender called it and renaming that would be worse.
const GENERIC_NAME = /^(image|img|clip_image|pasted[ _-]?image|screenshot)[ _-]?\d*\.(png|jpe?g|gif|bmp|webp)$/i;

const extFor = (att) => {
  const fromName = /\.([a-z0-9]{2,5})$/i.exec(att.filename || '');
  if (fromName) return fromName[1].toLowerCase();
  const sub = (att.mimeType || '').split('/')[1];
  return sub ? sub.split('+')[0].toLowerCase() : 'bin';
};

// A short, human-readable stem from the email's subject — enough to tell three screenshots apart.
const contextStem = (context) => {
  const s = String(context || '').trim().replace(/[^a-zA-Z0-9 _-]/g, '').replace(/\s+/g, ' ').trim();
  return s ? s.slice(0, 40) : null;
};

function baseNameFor(att, i, context) {
  const given = String(att.filename || '').trim();
  if (given && !GENERIC_NAME.test(given)) return safeName(given);
  const stem = contextStem(context);
  const kind = att.disposition === 'inline' ? 'inline' : 'attachment';
  return safeName(`${stem ? `${stem} - ` : ''}${kind} ${i + 1}.${extFor(att)}`);
}

// ⛔ AND GUARANTEE UNIQUENESS WHATEVER HAPPENS. Two genuinely different files can still share a
// name — two emails both carrying "notice.pdf", or a rename collision. A duplicate NAME is not a
// duplicate FILE (identical bytes were already collapsed by hash upstream), so both must survive
// and both must be identifiable.
function uniquify(name, taken) {
  if (!taken.has(name)) { taken.add(name); return name; }
  const dot = name.lastIndexOf('.');
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : '';
  for (let n = 2; n < 500; n += 1) {
    const candidate = `${stem} (${n})${ext}`;
    if (!taken.has(candidate)) { taken.add(candidate); return candidate; }
  }
  const fallback = `${stem} (${Date.now()})${ext}`;
  taken.add(fallback);
  return fallback;
}

/**
 * Wrap a browser File as a normalised attachment, so a file picked, dropped or pasted by hand goes
 * through the SAME staging path as one extracted from an email. One path, one set of guarantees.
 */
export async function fileToAttachment(file) {
  const bytes = new Uint8Array(await file.arrayBuffer());
  return {
    filename: file.name || null,
    mimeType: file.type || 'application/octet-stream',
    bytes,
    size: bytes.length,
    contentId: null,
    // A file someone chose is a document, not body decoration — it is never folded away.
    disposition: 'attachment',
  };
}

/**
 * Upload every attachment and create its file_exchange row. Creates NO task links — the caller does
 * that once the tasks exist.
 *
 * ⛔ ALL OR NOTHING. The first failure stops the run, deletes everything already staged, and
 * returns { error }. There is no partial success to report because a partial import is the bug.
 *
 * @returns {{ staged: Array<{fileExchangeId, storagePath, name, att}>, error: Error|null,
 *             skipped: number, duplicatesRemoved: number }}
 */
export async function stageEmailAttachments(attachments, { uploadedByName, context } = {}) {
  const all = Array.isArray(attachments) ? attachments : [];
  if (!all.length) return { staged: [], error: null, skipped: 0, duplicatesRemoved: 0 };

  // ⛔ DEDUPE FIRST, then cap. A fifty-message forwarded thread is usually ONE signature logo
  // repeated fifty times; collapsing identical bytes is lossless and normally takes the count far
  // under the cap on its own. Capping before deduping would refuse an import that was never
  // actually large.
  const { unique, duplicatesRemoved } = await dedupeAttachments(all);

  // ⛔ THE ONLY THING THAT EVER REFUSES AN IMAGE — a runaway guard, and it refuses the WHOLE import
  // rather than taking the first N. A task holding some of an email's images, with no indication
  // which are missing, is worse than a task the user had to retry.
  if (unique.length > MAX_ATTACHMENTS) {
    return {
      staged: [], skipped: unique.length, duplicatesRemoved,
      error: new Error(
        `This email carries ${unique.length} distinct attachments (after collapsing `
        + `${duplicatesRemoved} duplicate${duplicatesRemoved === 1 ? '' : 's'}), and the limit is `
        + `${MAX_ATTACHMENTS}. Nothing was imported — no task was created and no file was stored. `
        + 'Save the ones you need and attach them to the task by hand.'
      ),
    };
  }

  const batch = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const staged = [];
  const taken = new Set();

  for (let i = 0; i < unique.length; i += 1) {
    const att = unique[i];
    const name = uniquify(baseNameFor(att, i, context), taken);
    const storagePath = `tasks/_inbox/${batch}/${String(i).padStart(3, '0')}_${name}`;
    // uploadFile takes anything with bytes and a type; a Blob is exactly that, and it keeps the
    // Content-Type consistent with what gets signed.
    const blob = new Blob([att.bytes], { type: att.mimeType || 'application/octet-stream' });

    const { error: upErr } = await uploadFile(storagePath, blob);
    if (upErr) {
      await rollbackStaged(staged);
      return { staged: [], skipped: 0, duplicatesRemoved,
        error: new Error(`"${name}" failed to upload (${upErr.message}). Nothing was imported.`) };
    }

    // ⛔ CHECK THE INSERT. An RLS-blocked write returns zero rows and NO error, so `.select().single()`
    // is what proves a row exists — the absence of an error never has.
    const { data: fx, error: fxErr } = await supabase.from('file_exchange').insert({
      client_id: null, property_id: null, parcel_id: null,
      direction: 'ntrg_internal', folder_key: DOC_KEYS.taskAttachments,
      // ⛔ The distinguishing name, NOT att.filename. Storing the original would put three rows
      // called image001.png on one task — the whole reason baseNameFor exists.
      file_name: name, file_size_bytes: att.size, storage_path: storagePath,
      uploaded_by_name: uploadedByName || 'NTRG Staff',
      viewable_client: false, viewable_consultant: false, need_review: false,
    }).select('id').single();

    if (fxErr || !fx) {
      await rollbackStaged(staged);
      return { staged: [], skipped: 0, duplicatesRemoved,
        error: new Error(`"${name}" uploaded but could not be recorded (${fxErr?.message || 'no row returned'}). Nothing was imported.`) };
    }
    staged.push({ fileExchangeId: fx.id, storagePath, name, att });
  }

  return { staged, error: null, skipped: 0, duplicatesRemoved };
}

/**
 * Undo a partial stage. Best-effort by necessity — if cleanup itself fails there is nothing further
 * to try, and an orphaned object in R2 is recoverable debris while a wrongly-created task is not.
 * ⛔ Failures are logged rather than swallowed: silent cleanup is how orphans become invisible.
 */
export async function rollbackStaged(staged) {
  for (const s of staged || []) {
    const { error } = await fileDelete(s.fileExchangeId);
    if (error) console.warn(`[emailAttachments] rollback left "${s.name}" behind at ${s.storagePath}: ${error.message}`);
  }
}

/**
 * Link staged files to a task that now exists. Called once per created task.
 * ⛔ Checked, not fired-and-forgotten — a join insert that fails leaves a file nobody can reach
 * from the task it belongs to, which looks exactly like an attachment that was never there.
 */
export async function linkStagedToTask(staged, taskId, createdBy) {
  if (!taskId || !(staged || []).length) return { error: null };
  const rows = staged.map((s) => ({ task_id: taskId, file_exchange_id: s.fileExchangeId, created_by: createdBy || null }));
  const { data, error } = await supabase.from('task_attachments').insert(rows).select('id');
  if (error) return { error };
  if (!data || data.length !== rows.length) {
    return { error: new Error(`linked ${data?.length ?? 0} of ${rows.length} attachments`) };
  }
  return { error: null };
}

/** One line for the user, counting everything — absence is visible or it is a bug. */
export function stageSummary({ staged, duplicatesRemoved }) {
  const list = staged || [];
  if (!list.length) return null;
  const inline = list.filter((s) => s.att.disposition === 'inline');
  const files = list.filter((s) => s.att.disposition !== 'inline');
  const decorative = inline.filter((s) => looksDecorative(s.att));
  const bits = [`${list.length} file${list.length === 1 ? '' : 's'} attached`];
  if (duplicatesRemoved) bits.push(`${duplicatesRemoved} duplicate${duplicatesRemoved === 1 ? '' : 's'} collapsed`);
  if (inline.length) bits.push(`${inline.length} inline image${inline.length === 1 ? '' : 's'}`);
  if (decorative.length) bits.push(`${decorative.length} likely signature graphic${decorative.length === 1 ? '' : 's'}`);
  if (files.length) bits.push(`${files.length} document${files.length === 1 ? '' : 's'}`);
  return bits.join(' · ');
}
